/* ===================================================================
   reportPdf.ts -- build a real, downloadable PDF of the Reports screen.

   Item 1 (2026-07-04, Hopper): the reskin's "Export" button shipped wired
   to CSV only (the one real export at reskin time). This adds a genuine PDF
   export built with jsPDF (client-side, same as the CSV path -- no backend
   round trip, no new server dependency). The CSV export is kept alongside.

   HARD RULE -- NO FABRICATED METRICS. The PDF renders ONLY the real fields
   the /reports payload (ReportsResponse) and the live review list actually
   provide -- the exact same numbers the on-screen Reports body shows. Where
   a value is null (avg_rating / reply_rate with no data yet) we print "-",
   matching the screen. We never invent deltas, narratives, ranks, or themes
   the backend does not return.

   jsPDF core (text + vector primitives) only -- we do NOT touch .html() or
   the SVG path, so the optional canvg / dompurify / html2canvas deps are
   never imported and stay tree-shaken out of the bundle.
   =================================================================== */
import { jsPDF } from 'jspdf';
import type { ReportsResponse, ReportsSeriesPoint } from './api';

/* A rating distribution derived from the live review list, exactly as the
   Reports screen computes it (real per-star counts; never seeded). */
export interface RatingDistribution {
  /** counts[0] = 1-star ... counts[4] = 5-star. */
  counts: number[];
  total: number;
}

export interface ReportPdfInput {
  businessName: string;
  /** Human range label shown on the screen's range pill (e.g. "12 months"). */
  rangeLabel: string;
  /** The live /reports payload (KPIs + series + by_platform). */
  data: ReportsResponse;
  /** The real per-star distribution from the live review list. */
  ratingDist: RatingDistribution;
  /** The review-volume series already filtered to the selected range. */
  volumeSeries: ReportsSeriesPoint[];
}

/* Brand ink. Warm navy headings, muted gray body -- kept ASCII/hex only. */
const NAVY: [number, number, number] = [31, 58, 95]; // #1F3A5F
const INK: [number, number, number] = [32, 33, 36]; // near-black body
const MUTED: [number, number, number] = [95, 99, 104]; // #5f6368
const RULE: [number, number, number] = [218, 220, 224]; // #dadce0

function fmtInt(n: number): string {
  return Number.isFinite(n) ? Math.round(n).toLocaleString('en-US') : '0';
}

/* Build the PDF document object (does not trigger a download). Exposed so a
   caller can test the byte output; buildAndDownloadReportPdf wraps it. */
export function buildReportPdf(input: ReportPdfInput): jsPDF {
  const { businessName, rangeLabel, data, ratingDist, volumeSeries } = input;
  const kpis = data.kpis;

  const doc = new jsPDF({ unit: 'pt', format: 'letter' }); // 612 x 792 pt
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 48;
  const contentW = pageW - marginX * 2;
  let y = 56;

  // ---- Title block -------------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...NAVY);
  doc.text('Reputation report', marginX, y);

  y += 20;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  const name = (businessName || '').trim() || 'Your business';
  doc.text(name, marginX, y);

  y += 15;
  doc.setFontSize(9.5);
  const asOf = (() => {
    const d = data.as_of ? new Date(data.as_of) : new Date();
    return Number.isNaN(d.getTime()) ? new Date() : d;
  })();
  const generated = new Date();
  doc.text(
    `Range: ${rangeLabel}   |   As of ${asOf.toLocaleDateString('en-US')}   |   Generated ${generated.toLocaleDateString('en-US')}`,
    marginX,
    y,
  );

  y += 12;
  doc.setDrawColor(...RULE);
  doc.setLineWidth(1);
  doc.line(marginX, y, marginX + contentW, y);
  y += 28;

  // ---- KPI grid (2 x 2) --------------------------------------------------
  const avgRating = kpis.avg_rating;
  const replyRatePct = kpis.reply_rate != null ? Math.round(kpis.reply_rate * 100) : null;
  const kpiCards: { label: string; value: string; sub: string }[] = [
    {
      label: 'AVERAGE RATING',
      value: avgRating == null ? '-' : `${avgRating.toFixed(1)} / 5`,
      sub: `${fmtInt(kpis.avg_rating_n)} reviews`,
    },
    {
      label: 'NEW REVIEWS',
      value: fmtInt(kpis.new_reviews),
      sub: `Last ${fmtInt(kpis.new_reviews_window_weeks)} weeks`,
    },
    {
      label: 'RESPONSE RATE',
      value: replyRatePct == null ? '-' : `${replyRatePct}%`,
      sub: `${fmtInt(kpis.reply_rate_replied)}/${fmtInt(kpis.reply_rate_total)} replied`,
    },
    {
      label: 'TOTAL REVIEWS',
      value: fmtInt(kpis.total_reviews),
      sub: 'All time',
    },
  ];

  const gap = 16;
  const cardW = (contentW - gap) / 2;
  const cardH = 62;
  kpiCards.forEach((c, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = marginX + col * (cardW + gap);
    const cy = y + row * (cardH + gap);
    doc.setDrawColor(...RULE);
    doc.setLineWidth(1);
    doc.roundedRect(cx, cy, cardW, cardH, 6, 6);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(c.label, cx + 14, cy + 18);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...NAVY);
    doc.text(c.value, cx + 14, cy + 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(c.sub, cx + 14, cy + 55);
  });
  y += cardH * 2 + gap + 30;

  // ---- Rating breakdown --------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...NAVY);
  doc.text('Rating breakdown', marginX, y);
  y += 10;

  if (ratingDist.total === 0) {
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('No rated reviews yet.', marginX, y);
    y += 20;
  } else {
    y += 12;
    const barX = marginX + 60;
    const barW = contentW - 60 - 60;
    const rowH = 20;
    for (const star of [5, 4, 3, 2, 1]) {
      const count = ratingDist.counts[star - 1] ?? 0;
      const pct = ratingDist.total ? count / ratingDist.total : 0;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...INK);
      doc.text(`${star} star`, marginX, y + 10);
      // track
      doc.setFillColor(238, 240, 243); // #eef0f3
      doc.roundedRect(barX, y, barW, 10, 3, 3, 'F');
      // fill
      if (pct > 0) {
        doc.setFillColor(251, 188, 4); // gold #fbbc04
        doc.roundedRect(barX, y, Math.max(4, barW * pct), 10, 3, 3, 'F');
      }
      doc.setTextColor(...MUTED);
      doc.text(`${fmtInt(count)} (${Math.round(pct * 100)}%)`, barX + barW + 10, y + 10);
      y += rowH;
    }
    y += 12;
  }

  // ---- Review source -----------------------------------------------------
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...NAVY);
  doc.text('Review source', marginX, y);
  y += 8;

  const byPlatform = data.by_platform ?? [];
  const sourceRows =
    byPlatform.length > 0
      ? byPlatform.map((p) => ({ platform: p.platform, count: p.count }))
      : kpis.total_reviews > 0
        ? [{ platform: 'google', count: kpis.total_reviews }]
        : [];

  if (sourceRows.length === 0) {
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('No reviews to break down yet.', marginX, y);
    y += 20;
  } else {
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    for (const r of sourceRows) {
      const label = r.platform
        ? r.platform.charAt(0).toUpperCase() + r.platform.slice(1)
        : 'Other';
      doc.setTextColor(...INK);
      doc.text(label, marginX, y);
      doc.setTextColor(...MUTED);
      doc.text(fmtInt(r.count), marginX + contentW - 40, y, { align: 'right' });
      y += 18;
    }
    y += 8;
  }

  // ---- Review volume table (real series) ---------------------------------
  const points = (volumeSeries ?? []).filter((p) => p && p.label);
  if (points.length > 0) {
    // New page if we are running low.
    if (y > 640) {
      doc.addPage();
      y = 56;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...NAVY);
    doc.text('Review volume by period', marginX, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    y += 14;
    doc.text('Period', marginX, y);
    doc.text('Reviews', marginX + contentW - 40, y, { align: 'right' });
    y += 6;
    doc.setDrawColor(...RULE);
    doc.line(marginX, y, marginX + contentW, y);
    y += 14;

    doc.setFontSize(10);
    for (const p of points) {
      if (y > 750) {
        doc.addPage();
        y = 56;
      }
      doc.setTextColor(...INK);
      doc.text(String(p.label), marginX, y);
      doc.setTextColor(...MUTED);
      doc.text(p.value == null ? '-' : fmtInt(p.value), marginX + contentW - 40, y, {
        align: 'right',
      });
      y += 16;
    }
  }

  // ---- Footer on every page ---------------------------------------------
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    const footY = doc.internal.pageSize.getHeight() - 28;
    doc.text('Rhody Reviews', marginX, footY);
    doc.text(`Page ${i} of ${pageCount}`, pageW - marginX, footY, { align: 'right' });
  }

  return doc;
}

/* Build the PDF and trigger a browser download. Returns the filename used. */
export function buildAndDownloadReportPdf(input: ReportPdfInput): string {
  const doc = buildReportPdf(input);
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `rhody-reviews-report-${stamp}.pdf`;
  doc.save(filename);
  return filename;
}
